import { useState, useEffect } from "react";
import { formatStamp } from "../lib/richText";
import { useAuth } from "../contexts/AuthContext";
import { getClients, createClient, deleteClient, listMockups } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, UserCircle, Loader2, Building, Mail, Phone, ArrowRight, CheckCircle2, MessageSquare, Wand2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [newlyCreatedClient, setNewlyCreatedClient] = useState(null);
  // Number of homepage mockups per client — used to surface a badge + link on
  // each row so reps can jump straight to the mockups they've made.
  const [mockupCounts, setMockupCounts] = useState({});

  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
  });

  const fetchClients = async () => {
    try {
      const { data } = await getClients();
      setClients(data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // Load mockup counts (best-effort — reps in ops_coordinator role will
    // 403 on /api/mockups and the endpoint isn't strictly required)
    listMockups()
      .then((r) => {
        const counts = {};
        (r.data || []).forEach((m) => {
          if (m.client_id) counts[m.client_id] = (counts[m.client_id] || 0) + 1;
        });
        setMockupCounts(counts);
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await createClient(clientForm);
      setClientForm({ name: "", email: "", phone: "", company: "", notes: "" });
      setNewlyCreatedClient(data);
      toast.success("Client added successfully!");
      fetchClients();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create client";
      toast.error(typeof msg === "string" ? msg : "Failed to create client");
      console.error("Failed to create client:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!selectedClient) return;
    setSubmitting(true);
    try {
      await deleteClient(selectedClient.id);
      setDeleteDialogOpen(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      console.error("Failed to delete client:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-1">
            {user?.role === "manager" ? "All clients in your agency" : "Your clients"}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setNewlyCreatedClient(null);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            {newlyCreatedClient ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Client Added!
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-secondary/50 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                        {newlyCreatedClient.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold">{newlyCreatedClient.name}</p>
                        {newlyCreatedClient.company && (
                          <p className="text-sm text-muted-foreground">{newlyCreatedClient.company}</p>
                        )}
                      </div>
                    </div>
                    {newlyCreatedClient.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {newlyCreatedClient.email}
                      </div>
                    )}
                    {newlyCreatedClient.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {newlyCreatedClient.phone}
                      </div>
                    )}
                  </div>

                  <div className="border rounded-lg p-4 bg-primary/5">
                    <p className="text-sm font-medium mb-1">Next Step</p>
                    <p className="text-sm text-muted-foreground mb-3">Ready to place an order for this client?</p>
                    <Button
                      className="w-full"
                      onClick={() => {
                        setDialogOpen(false);
                        setNewlyCreatedClient(null);
                        navigate(`/orders/new?client=${newlyCreatedClient.id}`);
                      }}
                      data-testid="book-new-order-button"
                    >
                      Book a New Order
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setDialogOpen(false);
                      setNewlyCreatedClient(null);
                    }}
                    data-testid="close-confirmation-button"
                  >
                    Done — Stay on Clients
                  </Button>
                </div>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Add New Client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={clientForm.name}
                      onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                      placeholder="Client name"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={clientForm.email}
                      onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                      placeholder="client@example.com"
                      required
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        data-testid="client-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={clientForm.company}
                        onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                        placeholder="Company name"
                        data-testid="client-company-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={clientForm.notes}
                      onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows={3}
                      data-testid="client-notes-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-client-button">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      "Add Client"
                    )}
                  </Button>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Clients List */}
      {clients.length === 0 ? (
        <Card className="border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <UserCircle className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No clients yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Add your first client to get started</p>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-client-empty-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Your First Client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <UserCircle className="h-5 w-5" />
              All Clients ({clients.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  {user?.role === "manager" && <TableHead>Rep</TableHead>}
                  <TableHead>Added</TableHead>
                  <TableHead className="w-[110px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id} className="table-row-hover">
                    <TableCell>
                      <Link
                        to={`/orders/new?client=${client.id}`}
                        className="font-medium text-primary hover:underline"
                        data-testid={`client-link-${client.id}`}
                      >
                        {client.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {client.company ? (
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          {client.company}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          {client.email}
                        </div>
                        {client.phone && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            {client.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    {user?.role === "manager" && (
                      <TableCell className="text-muted-foreground">
                        {client.rep_name || "—"}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground">
                      {formatStamp(client.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-primary hover:text-primary hover:bg-primary/10 relative"
                          onClick={() => navigate(`/mockups?client=${client.id}`)}
                          title={mockupCounts[client.id]
                            ? `${mockupCounts[client.id]} homepage mockup${mockupCounts[client.id] > 1 ? "s" : ""} — click to view`
                            : "Build a homepage mockup for this client"}
                          data-testid={`client-mockups-${client.id}`}
                        >
                          <Wand2 className="h-4 w-4" />
                          {mockupCounts[client.id] > 0 && (
                            <span className="absolute -top-1 -right-1 bg-fuchsia-600 text-white rounded-full text-[9px] font-semibold h-3.5 min-w-[14px] px-0.5 flex items-center justify-center">
                              {mockupCounts[client.id]}
                            </span>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-primary hover:text-primary hover:bg-primary/10"
                          onClick={() => navigate(`/messages/${client.id}`)}
                          title="Open unified conversation (all orders for this client)"
                          data-testid={`client-messages-${client.id}`}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            setSelectedClient(client);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <strong>{selectedClient?.name}</strong> and all their projects. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClient}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-client"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
